import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/shadcn/tooltip'
import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { WordCloudConfig, WordCloudWord } from './types'
import { layoutWordCloud, PlacedWord } from './wordCloudLayout'
import { sizeWordCloudWords } from './wordCloudSizing'

export function WordCloudBody({
  config,
  words,
}: {
  config: WordCloudConfig
  words: WordCloudWord[]
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [placedWords, setPlacedWords] = useState<PlacedWord[]>([])
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 })

  const padding = config.padding ?? 8
  const sizedWords = useMemo(() => sizeWordCloudWords(words, config), [config, words])

  useLayoutEffect(() => {
    const container = containerRef.current
    if (!container) {
      return
    }

    const updateLayout = () => {
      const { width, height } = container.getBoundingClientRect()
      setContainerSize({ width, height })

      if (!width || !height || !sizedWords.length) {
        setPlacedWords([])
        return
      }

      const fontFamily = getComputedStyle(container).fontFamily
      setPlacedWords(layoutWordCloud(sizedWords, width, height, padding, fontFamily))
    }

    updateLayout()

    const observer = new ResizeObserver(updateLayout)
    observer.observe(container)

    return () => observer.disconnect()
  }, [padding, sizedWords])

  if (!sizedWords.length) {
    return (
      <div className="text-muted-foreground flex h-full items-center justify-center p-4">
        No data available
      </div>
    )
  }

  return (
    <TooltipProvider>
      <div ref={containerRef} className="relative h-full w-full overflow-hidden p-4">
        {placedWords.map((word) => (
          <Tooltip key={word.text}>
            <TooltipTrigger asChild>
              <span
                className="absolute cursor-default whitespace-nowrap font-semibold leading-none transition-opacity hover:opacity-60"
                style={{
                  left: word.x,
                  top: word.y,
                  fontSize: word.fontSize,
                  color: word.color,
                }}
              >
                {word.text}
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <p>
                {word.text}: {word.count}
              </p>
            </TooltipContent>
          </Tooltip>
        ))}
        {placedWords.length < sizedWords.length && containerSize.width > 0 && (
          <p className="text-muted-foreground absolute bottom-2 right-4 text-xs">
            {placedWords.length} of {sizedWords.length} words shown
          </p>
        )}
      </div>
    </TooltipProvider>
  )
}
